import type { FC, FormEvent, ReactNode } from 'react'
import type { ClinicSettings, TemplateKey } from '@/lib/clinic/types'

import { useMemo, useRef, useState } from 'react'
import { toast } from 'sonner'
import { RotateCcw } from 'lucide-react'
import { useClinic } from '@/hooks/use-clinic'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Textarea } from '@/components/ui/textarea'
import { SectionCard, SimpleSelect } from '@/components/app/common'
import { ChipButton, FormField, preventEnterSubmit } from '@/components/app/form-bits'
import { SaveBar } from '@/components/settings/clinic-settings'
import { DEFAULT_TEMPLATES, TEMPLATE_PLACEHOLDERS } from '@/lib/clinic/defaults'
import { MESSAGE_KIND_LABELS, toWhatsAppNumber } from '@/lib/clinic/format'
import { fillTemplate } from '@/lib/clinic/messaging'
import { addDays } from '@/lib/clinic/dates'

type FollowUpForm = ClinicSettings['followUp']

const TEMPLATE_INFO: Record<TemplateKey, string> = {
    reminder: 'Sent a day or two before the returning date.',
    overdue: 'For patients who missed their returning date.',
    confirmation: 'After the patient confirms a date and time.'
}

const TEMPLATE_KEYS: TemplateKey[] = ['reminder', 'overdue', 'confirmation']

interface TemplateEditorProps {
    templateKey: TemplateKey
    value: string
    onChange: (value: string) => void
    preview: string
}

const TemplateEditor: FC<TemplateEditorProps> = ({ templateKey, value, onChange, preview }): ReactNode => {
    const textRef = useRef<HTMLTextAreaElement>(null)

    const insert = (placeholder: string): void => {
        const field = textRef.current
        const start = field?.selectionStart ?? value.length
        const end = field?.selectionEnd ?? value.length
        const next = `${value.slice(0, start)}${placeholder}${value.slice(end)}`

        onChange(next)

        window.requestAnimationFrame(() => {
            field?.focus()
            field?.setSelectionRange(start + placeholder.length, start + placeholder.length)
        })
    }

    return (
        <div className='grid gap-4 rounded-2xl border bg-white p-4 lg:grid-cols-2'>
            <FormField
                label={MESSAGE_KIND_LABELS[templateKey]}
                htmlFor={`template-${templateKey}`}
                hint={TEMPLATE_INFO[templateKey]}
                labelAction={value !== DEFAULT_TEMPLATES[templateKey] ? (
                    <Button type='button' variant='ghost' size='xs' onClick={() => onChange(DEFAULT_TEMPLATES[templateKey])}>
                        <RotateCcw />
                        Restore default
                    </Button>
                ) : undefined}
            >
                <Textarea
                    ref={textRef}
                    id={`template-${templateKey}`}
                    value={value}
                    onChange={event => onChange(event.target.value)}
                    rows={4}
                    className='leading-relaxed'
                />

                <div className='flex flex-wrap gap-1.5 pt-1' aria-label='Insert a placeholder'>
                    {TEMPLATE_PLACEHOLDERS.map(placeholder => (
                        <ChipButton key={placeholder} onClick={() => insert(placeholder)} title={`Insert ${placeholder}`}>
                            {placeholder}
                        </ChipButton>
                    ))}
                </div>
            </FormField>

            <div className='grid content-start gap-1.5'>
                <p className='text-[0.8rem] font-medium text-foreground'>
                    Preview
                </p>

                <div className='rounded-2xl bg-[#efe7dd] p-3'>
                    <p className='ml-auto max-w-[92%] rounded-xl rounded-tr-sm bg-[#d9fdd3] px-3 py-2 text-sm leading-relaxed whitespace-pre-line text-slate-900 shadow-xs'>
                        {preview || 'Write a message to see the preview.'}
                    </p>
                </div>
            </div>
        </div>
    )
}

export const MessageSettingsPanel: FC = (): ReactNode => {
    const { data, index, today, updateSettings } = useClinic()
    const [form, setForm] = useState<FollowUpForm>(data.settings.followUp)
    const dirty = JSON.stringify(form) !== JSON.stringify(data.settings.followUp)

    const set = <K extends keyof FollowUpForm>(key: K, value: FollowUpForm[K]): void => {
        setForm(previous => ({ ...previous, [key]: value }))
    }

    const sampleValues = useMemo(() => {
        const pending = data.followUps.find(item => item.status === 'pending' && item.dueTime)
        const patient = pending ? index.patientsById.get(pending.patientId) : undefined

        return {
            patientName: patient?.name ?? 'Anjali Nair',
            date: pending?.dueDate ?? addDays(today, 7),
            time: pending?.dueTime ?? '10:30',
            treatment: pending?.reason ?? 'Root canal: second sitting',
            clinicName: data.settings.clinic.name,
            clinicPhone: form.contactPhone || data.settings.clinic.phone,
            doctorName: data.settings.clinic.doctorName
        }
    }, [data.followUps, data.settings.clinic, index, today, form.contactPhone])

    const submit = (event: FormEvent<HTMLFormElement>): void => {
        event.preventDefault()

        const interval = Math.round(Number(form.defaultIntervalDays))

        updateSettings(settings => ({
            ...settings,
            followUp: {
                ...form,
                defaultIntervalDays: Number.isFinite(interval) && interval > 0 ? Math.min(interval, 365) : 7,
                countryCode: form.countryCode.replace(/\D/g, '') || '91'
            }
        }))

        toast.success('Follow-up and message settings saved')
    }

    const exampleNumber = toWhatsAppNumber('98470 12345', form.countryCode)

    return (
        <form onSubmit={submit} onKeyDown={preventEnterSubmit} noValidate className='grid gap-6'>
            <SectionCard title='WhatsApp message templates' description='Placeholders in brackets are replaced with the real patient details when you send.'>
                <div className='grid gap-4'>
                    {TEMPLATE_KEYS.map(key => (
                        <TemplateEditor
                            key={key}
                            templateKey={key}
                            value={form.templates[key]}
                            onChange={value => set('templates', { ...form.templates, [key]: value })}
                            preview={fillTemplate(form.templates[key], sampleValues)}
                        />
                    ))}
                </div>
            </SectionCard>

            <SectionCard title='Default reminder messages' description='Which template is picked first when you open a reminder.'>
                <div className='grid gap-5 sm:grid-cols-2'>
                    <FormField label='For upcoming follow-ups' htmlFor='default-upcoming'>
                        <SimpleSelect
                            id='default-upcoming'
                            value={form.upcomingTemplate}
                            onValueChange={value => set('upcomingTemplate', value)}
                            options={TEMPLATE_KEYS.map(key => ({ value: key, label: MESSAGE_KIND_LABELS[key] }))}
                        />
                    </FormField>

                    <FormField label='For overdue follow-ups' htmlFor='default-overdue'>
                        <SimpleSelect
                            id='default-overdue'
                            value={form.overdueTemplate}
                            onValueChange={value => set('overdueTemplate', value)}
                            options={TEMPLATE_KEYS.map(key => ({ value: key, label: MESSAGE_KIND_LABELS[key] }))}
                        />
                    </FormField>
                </div>
            </SectionCard>

            <SectionCard title='Clinic contact for messages' description='Used for [Clinic Phone] and for opening WhatsApp chats.'>
                <div className='grid gap-5 sm:grid-cols-3'>
                    <FormField label='Contact number in messages' htmlFor='contact-phone' hint='Leave empty to use the clinic phone number.'>
                        <Input id='contact-phone' type='tel' value={form.contactPhone} onChange={event => set('contactPhone', event.target.value)} placeholder={data.settings.clinic.phone} />
                    </FormField>

                    <FormField label='Country code' htmlFor='country-code' hint={`Numbers without a code become ${exampleNumber}.`}>
                        <Input id='country-code' inputMode='numeric' value={form.countryCode} onChange={event => set('countryCode', event.target.value)} placeholder='91' />
                    </FormField>

                    <FormField label='Default follow-up after' htmlFor='default-interval' hint='Days, used when adding a follow-up.'>
                        <Input
                            id='default-interval'
                            type='number'
                            min={1}
                            max={365}
                            value={String(form.defaultIntervalDays)}
                            onChange={event => set('defaultIntervalDays', Number(event.target.value))}
                        />
                    </FormField>
                </div>

                <p className='mt-4 rounded-xl bg-muted/60 p-3 text-xs leading-relaxed text-muted-foreground'>
                    Reminders currently open WhatsApp on this computer or phone with the message ready, and a staff member presses send. Automatic scheduled reminders can be connected later through the WhatsApp Business API.
                </p>
            </SectionCard>

            <SaveBar dirty={dirty} onReset={() => setForm(data.settings.followUp)} />
        </form>
    )
}
